import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/hooks/useSEO";
import { submitContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Instagram, Facebook, Youtube, Mail, MapPin, Send, Phone } from "lucide-react";
import { IMAGES, SOCIAL, CONTACT } from "@/data/brand";

const TYPES = [
  { value: "school", label: "School / Daycare Booking" },
  { value: "sponsor", label: "Sponsorship Opportunity" },
  { value: "robotics", label: "Pluggy Robot Purchase / Quote" },
  { value: "content", label: "Custom Songs & Animations" },
  { value: "general", label: "General Inquiry" },
];

export default function Contact() {
  useSEO({ title: "Contact — Bookings, Sponsorships & Custom Content", description: "Contact The Safety Quest for school bookings, sponsorship opportunities, and custom children's songs and animations." });

  const [form, setForm] = useState({ name: "", email: "", organization: "", inquiry_type: "general", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent! Bobby & Pluggy will be in touch soon. 🚒");
      setForm({ name: "", email: "", organization: "", inquiry_type: "general", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="pt-36 pb-24 max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-4">Get in Touch</p>
          <h1 className="font-display text-6xl sm:text-7xl tracking-tight leading-[0.85]">Let's make kids safer, together.</h1>
          <p className="font-body text-lg text-sq-ink/70 mt-6 max-w-md">
            Booking a school visit, exploring sponsorship, or want a custom song or animation?
            Send us a note and we'll get right back to you.
          </p>

          <div className="mt-10 space-y-4">
            <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-sq-red/10 text-sq-red"><Phone size={20} /></span>
              <span className="font-medium group-hover:text-sq-red transition-colors">{CONTACT.phone}</span>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-sq-blue/10 text-sq-blue"><Mail size={20} /></span>
              <span className="font-medium group-hover:text-sq-blue transition-colors">{CONTACT.email}</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-sq-yellow/20 text-sq-ink"><MapPin size={20} /></span>
              <span className="font-medium">{CONTACT.location}</span>
            </div>
          </div>

          <div className="flex gap-3 mt-8">
            {[
              { Icon: Instagram, href: SOCIAL.instagram, label: "instagram" },
              { Icon: Facebook, href: SOCIAL.facebook, label: "facebook" },
              { Icon: Youtube, href: SOCIAL.youtube, label: "youtube" },
            ].map(({ Icon, href, label }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`contact-social-${label}`} className="grid place-items-center w-11 h-11 rounded-full bg-sq-ink text-white hover:bg-sq-red transition-colors">
                <Icon size={18} />
              </a>
            ))}
          </div>

          <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" data-testid="ig-qr-card" className="group mt-8 inline-flex items-center gap-4 rounded-2xl bg-white border-2 border-sq-ink p-3 pr-6 shadow-[0_5px_0_#111827] hover:-translate-y-0.5 transition-transform">
            <img src="/characters/ig_qr.png" alt="Scan to follow @bobbyandpluggy on Instagram" className="w-24 h-24 object-contain rounded-lg" />
            <div className="text-left">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-sq-red">Scan to follow</p>
              <p className="font-round text-lg font-semibold leading-tight">@bobbyandpluggy</p>
              <p className="text-sq-ink/50 text-sm">Follow along on Instagram</p>
            </div>
          </a>

          <div className="hidden lg:flex items-end gap-2 mt-10">
            <img src={IMAGES.bobby} alt="Bobby waving" className="w-40 object-contain" />
            <img src={IMAGES.pluggy} alt="Pluggy" className="w-24 object-contain" />
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="rounded-[2rem] bg-white border-2 border-sq-ink p-8 lg:p-10 shadow-[0_8px_0_#111827] space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold mb-2 block">Full Name *</label>
                <Input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="h-12 rounded-xl border-2 border-sq-ink/15" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-2 block">Email *</label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@school.org" className="h-12 rounded-xl border-2 border-sq-ink/15" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold mb-2 block">Organization</label>
                <Input data-testid="contact-org" value={form.organization} onChange={set("organization")} placeholder="Sunrise Daycare" className="h-12 rounded-xl border-2 border-sq-ink/15" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-2 block">Inquiry Type</label>
                <Select value={form.inquiry_type} onValueChange={(v) => setForm((f) => ({ ...f, inquiry_type: v }))}>
                  <SelectTrigger data-testid="contact-type" className="h-12 rounded-xl border-2 border-sq-ink/15"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold mb-2 block">Message *</label>
              <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="Tell us how we can help..." rows={5} className="rounded-xl border-2 border-sq-ink/15" />
            </div>

            <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full py-4 rounded-full bg-sq-red text-white font-bold text-lg btn-chunky inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? "Sending..." : <>Send Message <Send size={18} /></>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
